/**
 * NomSemaphore — Semáforo compacto NOMs para dashboard ejecutivo SGSSO.
 * Consume /api/hse/audits-normativa/compliance?year=YYYY y muestra un chip
 * verde/amarillo/rojo por norma con % anual. Marca en negrita las NOMs críticas.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldWarning, Clock, Warning } from "@phosphor-icons/react";

const LIGHT = {
  green: { bg: "bg-emerald-100", txt: "text-emerald-800", brd: "border-emerald-300", dot: "bg-emerald-500" },
  yellow: { bg: "bg-amber-100", txt: "text-amber-800", brd: "border-amber-300", dot: "bg-amber-500" },
  red: { bg: "bg-red-100", txt: "text-red-800", brd: "border-red-300", dot: "bg-red-500" },
};

const LEVEL_STYLE = {
  CRITICAL: { icon: ShieldWarning, cls: "bg-red-50 border-red-300 text-red-800" },
  HIGH: { icon: Clock, cls: "bg-orange-50 border-orange-300 text-orange-800" },
  MEDIUM: { icon: Warning, cls: "bg-amber-50 border-amber-300 text-amber-800" },
};

export default function NomSemaphore({ year }) {
  const [comp, setComp] = useState(null);
  const [busy, setBusy] = useState(true);
  const y = year || new Date().getFullYear();

  useEffect(() => {
    setBusy(true);
    api.get(`/hse/audits-normativa/compliance?year=${y}`)
      .then((r) => setComp(r.data))
      .catch(() => setComp(null))
      .finally(() => setBusy(false));
  }, [y]);

  if (busy) return <p className="text-xs text-slate-400 italic">Cargando semáforo NOMs…</p>;
  if (!comp || !comp.by_norma) return null;
  if (comp.by_norma.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-4 text-xs text-slate-500 italic"
            data-testid="nom-semaphore-empty">
        Sin plan de auditoría normativa cargado para {y}. Ve a HSE → Auditoría Normativa → Plan.
      </div>
    );
  }

  const t = comp.totals || {};
  const alerts = comp.alerts || [];
  const critical = alerts.filter((a) => a.level === "CRITICAL");
  const high = alerts.filter((a) => a.level === "HIGH");

  return (
    <section data-testid="nom-semaphore" className="space-y-2">
      <div className="flex items-baseline justify-between">
        <div>
          <p className="font-heading font-bold text-slate-900">Semáforo NOMs · Auditoría Normativa</p>
          <p className="text-xs text-slate-500">
            {t.pct_anual ?? 0}% cumplimiento anual ·
            <span className={`ml-1 font-semibold ${t.light === "green" ? "text-emerald-700" : t.light === "yellow" ? "text-amber-700" : "text-red-700"}`}>
              {t.light === "green" ? "En control" : t.light === "yellow" ? "Vigilar" : "Crítico"}
            </span>
            <span className="text-slate-400"> · {t.cumplidas || 0}/{t.planeadas || 0} actividades cumplidas</span>
          </p>
        </div>
        <a href="/hse?tab=audits-normativa" className="text-xs text-blue-800 hover:underline"
           data-testid="nom-semaphore-link">Ver detalle →</a>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
        {comp.by_norma.map((n) => {
          const st = LIGHT[n.light] || LIGHT.yellow;
          return (
            <div key={n.norma}
                 className={`border rounded-md p-2 ${st.bg} ${st.brd}`}
                 data-testid={`nom-chip-${n.norma}`}
                 title={`${n.norma} · ${n.cumplidas}/${n.planeadas} · ${n.pct_anual}%`}>
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                <span className={`inline-block w-2 h-2 rounded-full ${st.dot}`} />
                {n.is_critical ? <span className="text-red-700">◉</span> : null}
                {n.norma}
              </div>
              <p className={`font-heading font-black text-lg ${st.txt}`}>{n.pct_anual}%</p>
              <p className="text-[10px] text-slate-500">{n.cumplidas}/{n.planeadas}</p>
            </div>
          );
        })}
      </div>

      {(critical.length + high.length) > 0 && (
        <div className="grid gap-1.5">
          {[...critical, ...high].slice(0, 4).map((a, i) => {
            const L = LEVEL_STYLE[a.level] || LEVEL_STYLE.MEDIUM;
            const Icon = L.icon;
            return (
              <div key={i} className={`flex gap-2 items-start border rounded-md px-2.5 py-1.5 text-[11px] ${L.cls}`}
                    data-testid={`nom-alert-${i}`}>
                <Icon size={14} weight="bold" className="mt-0.5 shrink-0" />
                <span className="leading-snug">{a.message}</span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
